<template>
    <div>
        <!-- Hero banner -->
        <div class="bg-[#020b2d] text-white py-16 px-4">
            <div class="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <!-- Left: text -->
                <div>
                    <h1 class="text-3xl md:text-4xl font-bold tracking-tight leading-tight">{{ $t('system_update_title') }}</h1>
                    <p class="mt-3 text-blue-200 leading-relaxed">
                        {{ $t('system_update_intro_prefix') }}
                        <span v-if="formattedFrom">
                            {{ $t('system_update_from_label') }} <strong class="text-white">{{ formattedFrom }}</strong></span>{{ ' ' }}<span v-if="formattedTo">
                            {{ $t('system_update_to_label') }} <strong class="text-white">{{ formattedTo }}</strong></span
                        >.
                        {{ $t('system_update_intro_suffix') }}
                    </p>
                </div>

                <!-- Right: image -->
                <div class="flex justify-center lg:justify-end">
                    <img
                        :src="$asset('/hkcm/images/sorry-under-construction.png', 'public')"
                        :alt="$t('system_update_image_alt')"
                        class="w-full max-w-[420px] h-auto object-contain"
                    />
                </div>
            </div>
        </div>

        <!-- Content section (light background) -->
        <div class="bg-gray-50 py-12 px-4">
            <div class="max-w-5xl mx-auto space-y-8">
                <!-- Services grid -->
                <div>
                    <p class="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">
                        {{ $t('system_update_unavailable_heading') }}
                    </p>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div
                            class="group bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-default"
                        >
                            <div class="flex items-start gap-4">
                                <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors duration-200">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="w-5 h-5 text-blue-600"
                                    >
                                        <rect width="20" height="14" x="2" y="5" rx="2" />
                                        <path d="M2 10h20" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-gray-900 text-sm">{{ $t('system_update_service_subscriptions_title') }}</h3>
                                    <p class="mt-1 text-gray-500 text-sm leading-relaxed">{{ $t('system_update_service_subscriptions_desc') }}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            class="group bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-default"
                        >
                            <div class="flex items-start gap-4">
                                <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors duration-200">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="w-5 h-5 text-blue-600"
                                    >
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                        <circle cx="12" cy="7" r="4" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-gray-900 text-sm">{{ $t('system_update_service_profile_title') }}</h3>
                                    <p class="mt-1 text-gray-500 text-sm leading-relaxed">{{ $t('system_update_service_profile_desc') }}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            class="group bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-default"
                        >
                            <div class="flex items-start gap-4">
                                <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors duration-200">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="w-5 h-5 text-blue-600"
                                    >
                                        <polyline points="20 12 20 22 4 22 4 12" />
                                        <rect width="20" height="5" x="2" y="7" />
                                        <line x1="12" x2="12" y1="22" y2="7" />
                                        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                                        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-gray-900 text-sm">{{ $t('system_update_service_gifting_title') }}</h3>
                                    <p class="mt-1 text-gray-500 text-sm leading-relaxed">{{ $t('system_update_service_gifting_desc') }}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            class="group bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-default"
                        >
                            <div class="flex items-start gap-4">
                                <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors duration-200">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="w-5 h-5 text-blue-600"
                                    >
                                        <rect width="20" height="16" x="2" y="4" rx="2" />
                                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-gray-900 text-sm">{{ $t('system_update_service_newsletter_title') }}</h3>
                                    <p class="mt-1 text-gray-500 text-sm leading-relaxed">{{ $t('system_update_service_newsletter_desc') }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ETA -->
                <div class="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-start gap-4">
                    <div class="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center mt-0.5">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="w-4 h-4 text-blue-600"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                    </div>
                    <p class="text-gray-600 text-sm leading-relaxed">
                        {{ $t('system_update_eta_text') }}<br />
                        <span class="mt-1 block"
                            >{{ $t('system_update_eta_phase_label') }}
                            <strong class="text-gray-900">{{ formattedFrom || '–' }}</strong>{{ ' ' }}<span v-if="formattedTo">
                                {{ $t('system_update_to_label') }} <strong class="text-gray-900">{{ formattedTo }}</strong>
                            </span></span
                        >
                    </p>
                </div>

                <!-- Support CTA -->
                <div class="grid grid-cols-1 gap-4">
                    <div class="bg-[#353CEE] rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div class="flex items-start gap-3">
                            <div class="flex-shrink-0 w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="w-5 h-5 text-white"
                                >
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                </svg>
                            </div>
                            <div>
                                <h3 class="font-semibold text-white text-sm leading-snug">{{ $t('system_update_support_title') }}</h3>
                                <p class="text-white/75 text-sm leading-relaxed mt-1">
                                    {{ $t('system_update_support_desc') }}
                                </p>
                            </div>
                        </div>
                        <a
                            :href="`mailto:${supportEmail}`"
                            class="flex-shrink-0 inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-900 font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-colors duration-150"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="w-4 h-4"
                            >
                                <rect width="20" height="16" x="2" y="4" rx="2" />
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                            </svg>
                            {{ $t('system_update_support_button') }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    computed: {
        supportEmail() {
            return window?.meta?.settings?.company_contact_email || 'info@hkcmanagement.de';
        },
        formattedFrom() {
            return this.formatDate(window?.meta?.settings?.legacy_system_block?.from);
        },
        formattedTo() {
            return this.formatDate(window?.meta?.settings?.legacy_system_block?.to);
        }
    },
    methods: {
        formatDate(isoString) {
            if (!isoString) return null;

            const date = new Date(isoString);
            const locale = document.documentElement.lang?.startsWith('en') ? 'en-US' : 'de-DE';

            return (
                date.toLocaleString(locale, {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }) +
                ' ' +
                this.$t('system_update_clock_suffix')
            );
        }
    }
};
</script>
